import React from 'react';
import { X, Activity } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface SeismicRiskProperties {
  id: string | number;
  pga: number | null;
}

interface Props {
  point: SeismicRiskProperties;
  onClose: () => void;
}

function categoryFor(pga: number | null): { label: string; color: string } {
  if (pga == null || !isFinite(pga)) return { label: 'Unknown', color: '#6b7280' };
  if (pga < 2)  return { label: 'Very Low',  color: '#22c55e' };
  if (pga < 5)  return { label: 'Low',       color: '#84cc16' };
  if (pga < 15) return { label: 'Moderate',  color: '#eab308' };
  if (pga < 40) return { label: 'High',      color: '#f97316' };
  return { label: 'Very High', color: '#ef4444' };
}

export const SeismicRiskDetail: React.FC<Props> = ({ point, onClose }) => {
  const { theme } = useTheme();
  const pga = point.pga != null ? Number(point.pga) : null;
  const cat = categoryFor(pga);

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Activity size={18} color={cat.color} />
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>Seismic Risk</h2>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        <div style={{
          padding: '10px 14px', marginBottom: '16px', borderRadius: '8px',
          background: cat.color + '15', border: `1px solid ${cat.color}30`,
        }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: cat.color, marginBottom: '4px' }}>
            {cat.label} Seismic Hazard {pga != null ? `(${pga.toFixed(1)}%)` : ''}
          </div>
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.5 }}>
            Probability of damaging ground shaking (Modified Mercalli Intensity VI or greater) in the next
            100 years. Basis for the design earthquake in building and equipment codes.
          </div>
        </div>

        <Section title="Hazard Metric">
          {pga != null && <Field label="100-yr MMI VI+ probability" value={`${pga.toFixed(2)}%`} />}
          <Field label="Category" value={cat.label} />
        </Section>

        <Section title="BTM Engineering Impact">
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.7 }}>
            <ImpactItem
              label="Foundation Design"
              detail={cat.label === 'High' || cat.label === 'Very High'
                ? 'Site Class-specific design earthquake required. Reinforced mat or pile foundations, code-compliant seismic detailing. Added cost: 5–12% of civil/structural.'
                : 'Standard IBC seismic detailing. No incremental cost over baseline.'}
              theme={theme}
            />
            <ImpactItem
              label="Equipment Anchoring"
              detail={cat.label === 'High' || cat.label === 'Very High'
                ? 'Generators, transformers, switchgear, cooling units all require seismic anchorage per ASCE 7 or IEEE 693. Added engineering + hardware: $200K–$1M per site.'
                : 'Standard anchorage. No derating.'}
              theme={theme}
            />
            <ImpactItem
              label="Pipeline Crossings"
              detail="Active fault crossings require flexible couplings + automated shutoff valves. For most of TX/NM/AZ/WV, low PGA values mean this is not a driver."
              theme={theme}
            />
            <ImpactItem
              label="Insurance"
              detail="Seismic is typically covered under property all-risk, but deductibles step up with PGA. Below 5% PGA: no discernible premium impact."
              theme={theme}
            />
          </div>
        </Section>

        <Section title="Data Source">
          <div style={{ fontSize: '11px', color: theme.textTertiary, lineHeight: 1.6 }}>
            USGS National Seismic Hazard Model 2023 · 0.05° grid ·
            <a href="https://www.usgs.gov/programs/earthquake-hazards/science/2023-conterminous-us-national-seismic-hazard-model" target="_blank" rel="noopener noreferrer" style={{ color: designTokens.colors.brand, marginLeft: '4px' }}>docs</a>
          </div>
        </Section>
      </div>
    </div>
  );
};

const ImpactItem: React.FC<{ label: string; detail: string; theme: ReturnType<typeof useTheme>['theme'] }> = ({ label, detail, theme }) => (
  <div style={{ marginBottom: '10px', paddingLeft: '10px', borderLeft: `2px solid ${theme.borderLight}` }}>
    <div style={{ fontSize: '12px', fontWeight: 600, color: theme.text, marginBottom: '2px' }}>{label}</div>
    <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.5 }}>{detail}</div>
  </div>
);
